import { View, Text, StyleSheet } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import Colors from "../../../Resources/Color";
import { useTranslation } from "react-i18next";

/**
 * PlatformPreview Component
 * Shows a minimalistic preview of the platform with sidebar and main area
 */
const PlatformPreview = ({
  selectedFeatures = [],
  logoUri,
  athleteTypes = [],
  showLogo = false,
}) => {
  const { t } = useTranslation();
  return (
    <View style={styles.previewCard}>
      <View style={styles.sidebar}>
        <View style={styles.sidebarLargeRow}>
          <View style={styles.sidebarAvatar} />
          <View style={styles.sidebarLargeItem} />
        </View>

        <View style={styles.sidebarSmallRow}>
          <View style={styles.sidebarAvatarSmall} />
          <View style={styles.sidebarSmallItem} />
        </View>

        <View style={styles.sidebarSmallRow}>
          <View style={styles.sidebarAvatarSmall} />
          <View style={styles.sidebarSmallItem} />
        </View>

        <View style={styles.sidebarSmallRow}>
          <View style={styles.sidebarAvatarSmall} />
          <View style={styles.sidebarSmallItem} />
        </View>

        {selectedFeatures.includes("competitions") &&
          athleteTypes.includes("powerlifters") && (
            <View style={styles.sidebarIconRow}>
              <Ionicons
                name="podium-outline"
                size={16}
                color={Colors.bubbleColor}
                style={styles.sidebarIcon}
              />
              <Text style={styles.sidebarLabel}>
                {t("competitions-string")}
              </Text>
            </View>
          )}

        {selectedFeatures.includes("billing") && (
          <View style={styles.sidebarIconRow}>
            <Ionicons
              name="card-outline"
              size={16}
              color={Colors.bubbleColor}
              style={styles.sidebarIcon}
            />
            <Text style={styles.sidebarLabel}>{t("billing-string")}</Text>
          </View>
        )}

        {selectedFeatures.includes("applications") && (
          <View style={styles.sidebarIconRow}>
            <Ionicons
              name="person-outline"
              size={16}
              color={Colors.bubbleColor}
              style={styles.sidebarIcon}
            />
            <Text style={styles.sidebarLabel}>{t("applications-string")}</Text>
          </View>
        )}
      </View>

      <View style={styles.mainArea}>
        {/* Logo in top right corner - only show when showLogo is true */}
        {showLogo && (
          <View
            style={[
              styles.logoContainer,
              {
                backgroundColor: logoUri
                  ? "transparent"
                  : Colors.lightGrayColor,
              },
            ]}
            // Mask logo preview in Clarity recordings
            nativeID="logo-preview"
            dataSet={{ clarityMask: "true" }}
          >
            {logoUri ? (
              <img
                src={logoUri}
                alt="Logo preview"
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: 12,
                  objectFit: "contain",
                }}
              />
            ) : (
              <Text style={styles.logoPlaceholderSmall}>
                {t("onboardingLogoPlaceholder-string")}
              </Text>
            )}
          </View>
        )}

        {/* Chats panel when videos is selected */}
        {selectedFeatures.includes("videos") && (
          <View style={styles.chatsPanel}>
            <Text style={styles.chatsPanelText}>Chats</Text>
          </View>
        )}

        {/* Main content area - can be customized with children or props in the future */}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  previewCard: {
    width: 700,
    height: 500,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    flexDirection: "row",
    overflow: "hidden",
  },
  sidebar: {
    width: 160,
    borderRightWidth: 1,
    borderRightColor: Colors.borderColor,
    paddingVertical: 24,
    paddingHorizontal: 20,
    gap: 16,
    backgroundColor: Colors.lightGrayColor,
  },
  sidebarLargeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 8,
  },
  sidebarAvatar: {
    width: 24,
    height: 24,
    borderRadius: 16,
    backgroundColor: Colors.blackColor,
    flexShrink: 0,
    opacity: 0.05,
  },
  sidebarLargeItem: {
    height: 12,
    width: "60%",
    borderRadius: 6,
    backgroundColor: Colors.blackColor,
    opacity: 0.1,
  },
  sidebarSmallRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  sidebarAvatarSmall: {
    width: 16,
    height: 16,
    borderRadius: 12,
    backgroundColor: Colors.blackColor,
    opacity: 0.05,
    flexShrink: 0,
  },
  sidebarSmallItem: {
    height: 8,
    width: "60%",
    borderRadius: 5,
    backgroundColor: Colors.blackColor,
    opacity: 0.1,
  },
  sidebarIconRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  sidebarIcon: {
    flexShrink: 0,
  },
  sidebarLabel: {
    fontSize: 12,
    color: Colors.blackColor,
    fontWeight: "500",
    flex: 1,
  },
  mainArea: {
    flex: 1,
    // paddingVertical: 28,
    // paddingHorizontal: 32,
    gap: 24,
    backgroundColor: Colors.whiteColor,
    position: "relative",
  },
  logoContainer: {
    position: "absolute",
    top: 20,
    right: 20,
    width: 68,
    height: 68,
    borderRadius: 34,
    backgroundColor: Colors.lightGrayColor,
    justifyContent: "center",
    alignItems: "center",
  },
  logoPlaceholderSmall: {
    fontSize: 8,
    color: Colors.bubbleColor,
    fontWeight: "600",
  },
  chatsPanel: {
    position: "absolute",
    bottom: 0,
    left: 16,
    backgroundColor: Colors.whiteColor,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 10,
    minWidth: 180,
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: Colors.borderColor,
  },
  chatsPanelText: {
    fontSize: 12,
    fontWeight: "600",
    color: Colors.blackColor,
  },
});

export default PlatformPreview;
